import type React from 'react';

import type { ExtensionManifest, MaybeADFEntity } from '@atlaskit/editor-common/extensions';
import {
	ROVO_SKILL_EXTENSION_KEY,
	ROVO_SKILL_EXTENSION_TYPE,
} from '@atlassian/conversation-assistant-content-transformer/utils/skills';
import {
	defaultIcon,
	skillIcon,
	type SkillIconName,
} from '@atlassian/rovo-platform-ui-components/skills/skill-icons';
import type { Skill } from '@atlassian/rovo-platform-ui-components/skills/types';

const DEFAULT_CATEGORY = 'AI';
const MAX_DESCRIPTION_LENGTH = 150;

/**
 * Truncate description to a maximum length.
 * Used to keep insert menu items readable when skill descriptions are long.
 */
function truncateDescription(description: string): string {
	if (description.length <= MAX_DESCRIPTION_LENGTH) {
		return description;
	}
	return description.slice(0, MAX_DESCRIPTION_LENGTH).trim() + '…';
}

/**
 * Returns the skill-specific icon based on the iconName field.
 * Falls back to the default skill icon if no match is found.
 */
const getSkillIcon = (iconName?: SkillIconName) =>
	iconName && skillIcon[iconName] ? skillIcon[iconName] : defaultIcon;

/**
 * Options for customizing how skills are rendered in the manifest.
 * Both product editors and Rovo Chat can use this to configure the manifest.
 */
export type BuildSkillsManifestOptions = {
	/**
	 * Called when a skill is selected. Return value is passed back to the editor.
	 * - Product editors: call `onSkillSelected` then return void
	 * - Rovo Chat: return `createSkillNode(skill)` to insert the ADF node
	 */
	onAction?: (skill: Skill) => MaybeADFEntity | Promise<MaybeADFEntity>;

	/**
	 * Categories for the quick insert items.
	 * @default ['AI']
	 */
	categories?: string[];

	/**
	 * Priority for quick insert items (lower = higher priority).
	 * @default undefined (uses editor default of 1)
	 */
	priority?: number;

	/**
	 * Custom lozenge renderer per skill (e.g. Beta badge).
	 * Return undefined to show no lozenge.
	 */
	getLozenge?: (skill: Skill) => React.ReactNode | undefined;

	/**
	 * Custom node renderers for the extension (e.g. SkillTag chip rendering).
	 * If provided, added to `modules.nodes` in the manifest.
	 */
	nodes?: ExtensionManifest['modules']['nodes'];
};

/**
 * Builds an ExtensionManifest from a list of skills.
 *
 * This is the shared, pure function that both `createRovoSkillsExtensionManifest`
 * (for product editors) and `useSkillsManifest` (for Rovo Chat) can call.
 * It uses the same `key` and `type` constants so that `inlineExtension` nodes
 * render correctly across both contexts.
 *
 * @param skills - The skills to include in the manifest
 * @param options - Optional configuration for customizing the manifest
 *
 * @example
 * ```typescript
 * // Product editor usage:
 * buildSkillsManifest(skills, {
 *   onAction: (skill) => { openInlinePrompt(skill); },
 * });
 *
 * // Rovo Chat usage:
 * buildSkillsManifest(skills, {
 *   onAction: (skill) => createSkillNode(skill),
 *   categories: ['Skills'],
 *   priority: 99,
 *   getLozenge: (skill) => skill.tags?.includes('beta') ? <BetaLozenge /> : undefined,
 *   nodes: { skillTag: { type: 'inlineExtension', render: ... } },
 * });
 * ```
 */
export function buildSkillsManifest(
	skills: Skill[],
	options: BuildSkillsManifestOptions = {},
): ExtensionManifest {
	const { onAction, categories = [DEFAULT_CATEGORY], priority, getLozenge, nodes } = options;

	const quickInsertModules = skills.map((skill) => {
		const keywords: string[] = [skill.slug, skill.product, skill.name, 'skill', 'skills'].filter(
			(k): k is string => typeof k === 'string' && k.length > 0,
		);

		return {
			key: skill.slug,
			title: skill.name,
			description: truncateDescription(skill.description ?? ''),
			keywords,
			categories,
			...(priority !== undefined && { priority }),
			icon: getSkillIcon(skill.iconName),
			...(getLozenge && { lozenge: getLozenge(skill) }),
			action: async (): Promise<MaybeADFEntity> => onAction?.(skill),
		};
	});

	return {
		title: 'Rovo Skills',
		description: 'AI-powered skills from Rovo',
		type: ROVO_SKILL_EXTENSION_TYPE,
		key: ROVO_SKILL_EXTENSION_KEY,
		icons: {
			'16': defaultIcon,
			'24': defaultIcon,
			'48': defaultIcon,
		},
		modules: {
			quickInsert: quickInsertModules,
			...(nodes && { nodes }),
		},
	};
}
